import { useState } from 'react';
import cn from 'classnames';
import QRCode from 'qrcode.react';
// Hooks
import { useGetSocialLinks } from 'hooks';
// Helpers
import { getEnvLink } from 'helpers/routes';
// Ui
import Icon from 'ui/Icon/Icon';
// Ui3
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Button, { ButtonSize } from 'ui3/Button/Button';
import Modal from 'ui3/Modal/Modal';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './ShareQRButton.module.scss';

type ShareQRButtonProps = {
  slug: string;
  storeName: string;
  wrapperClassName?: string;
  hideText?: boolean;
  size?: ButtonSize;
};

const ShareQRButton = ({
  slug,
  storeName,
  wrapperClassName,
  hideText = false,
  size = 'medium',
}: ShareQRButtonProps): JSX.Element => {
  const [isVisible, setVisibility] = useState<boolean>(false);

  const { facebookShareLink, twitterShareLink } = useGetSocialLinks({
    url: `/${slug}`,
  });

  const sharePathname = `${getEnvLink()}/${slug}`;

  const handleModalOpen = () => {
    setVisibility(true);
  };

  const handleModalClose = () => {
    setVisibility(false);
  };

  const handleCopyLinkButtonClick = async () => {
    try {
      await navigator.clipboard.writeText(sharePathname);

      showToast({
        message: 'Copied',
        autoClose: 1000,
      });
    } catch (error) {
      showToast({
        message: 'Could not copy text',
        type: 'error',
        autoClose: 1000,
      });
    }
  };

  return (
    <div className={cn(styles.root, wrapperClassName)}>
      <Button
        variant="secondary"
        size={size}
        className={styles.shareButton}
        onClick={handleModalOpen}
      >
        <MillionsIcon name="share" className={styles.icon} />
        {hideText ? '' : 'Share'}
      </Button>
      <Modal
        title="Share"
        subTitle="Share this profile in any way convenient for you"
        size="small"
        open={isVisible}
        onClose={handleModalClose}
        focusTrapped={false}
      >
        <div className={styles.entry}>
          <div className={styles.qrWrapper}>
            <QRCode
              value={sharePathname}
              size={175}
              bgColor="#ffffff"
              fgColor="#0a0a0c"
              renderAs="svg"
              level="H"
              imageSettings={{
                src: 'images/qr-code/qr-code-logo.png',
                height: 40,
                width: 40,
                excavate: true,
              }}
            />
          </div>
          <span className={styles.tag}>{storeName}</span>
          <div className={styles.socialLinks}>
            <div className={styles.socialItem}>
              <a
                className={styles.socialButton}
                href={twitterShareLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                <MillionsIcon name="x" />
              </a>
              <span className={styles.itemName}>X</span>
            </div>
            <div className={styles.socialItem}>
              <a
                className={styles.socialButton}
                href={facebookShareLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                <MillionsIcon name="facebook" />
              </a>
              <span className={styles.itemName}>Facebook</span>
            </div>
            <div className={styles.socialItem}>
              <button
                className={styles.socialButton}
                onClick={handleCopyLinkButtonClick}
              >
                <Icon name="link" withoutFill />
              </button>
              <span className={styles.itemName}>Copy Link</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShareQRButton;
